/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { sortBy } from "lodash/fp";
import { MeasurementStatistics } from "@/presentation/MeasurementCharacteristic/statistics";
import { MeasurementSortDirection } from "./rowOrder";

/**
 * Sorting for the measurement *summary* table only. The detail
 * table has its own columns and its own sorter in ./measurements - deliberately
 * kept apart so neither table's cases leak into the other's.
 *
 * The two tables stay in step through the shared row order (see ./rowOrder),
 * not through a shared sort function.
 */
export const statisticsSortColumns = [
  "IG",
  "Feature",
  "Name",
  "Count",
  "Mean",
  "Minimum",
  "Maximum",
  "Range",
  "Std",
  "Pp",
  "Ppk",
  "Graph",
] as const;

export type StatisticsSortColumn = (typeof statisticsSortColumns)[number];

/** The column each sortable heading reads from a statistics row. */
const columnProperties: Record<
  StatisticsSortColumn,
  keyof MeasurementStatistics
> = {
  IG: "intelligentGroupCount",
  Feature: "featureName",
  Name: "name",
  Count: "count",
  Mean: "mean",
  Minimum: "min",
  Maximum: "max",
  Range: "range",
  Std: "std",
  Pp: "pp",
  Ppk: "ppk",
  Graph: "ppk",
};

export function isStatisticsSortColumn(
  column: string
): column is StatisticsSortColumn {
  return (statisticsSortColumns as readonly string[]).includes(column);
}

/** Sorts the measurement summary table's rows by one of its own columns. */
export function sortStatistics(
  rows: readonly MeasurementStatistics[],
  sortColumn: string,
  sortDirection: MeasurementSortDirection
): MeasurementStatistics[] {
  const property = isStatisticsSortColumn(sortColumn)
    ? columnProperties[sortColumn]
    : "name";

  const sorted = sortBy((row: MeasurementStatistics) => row[property] ?? "")([
    ...rows,
  ]);

  return sortDirection === "desc" ? sorted.reverse() : sorted;
}
